import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useNavigate } from "react-router-dom";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import FilePicker from "components/FilePicker";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import {
  bulkUploadChallenges,
  downloadChallengeTemplate,
} from "services/challengeService";

type UploadMode = "individual" | "combo";

interface BulkError {
  row?: number;
  sheet?: string;
  comboGroup?: string;
  field?: string;
  message: string;
}

interface BulkResult {
  success: boolean;
  created: number;
  failed: number;
  createdChallengeIds?: string[];
  errors?: BulkError[];
}

const MODE_COPY: Record<
  UploadMode,
  { label: string; accent: string; icon: React.ReactNode; description: string; fileLabel: string }
> = {
  individual: {
    label: "Individual",
    accent: "#2563eb",
    icon: <LayersOutlinedIcon />,
    description:
      "Create multiple Individual challenges at once — each row in the sheet becomes one challenge.",
    fileLabel: "Select Individual Challenges XLSX",
  },
  combo: {
    label: "Combo",
    accent: "#7c3aed",
    icon: <AccountTreeOutlinedIcon />,
    description:
      'Create multiple Combo challenges at once. Each combo has one main row (e.g. "Combo_1") for the ' +
      'combo-level fields, followed by its sub-challenge rows ("Combo_1_Sub_1", "Combo_1_Sub_2", …). ' +
      "Title translations go in the per-language columns at the end of that same row.",
    fileLabel: "Select Combo Challenges XLSX",
  },
};

// Numbered, accented step card.
const SectionCard = ({
  step,
  title,
  subtitle,
  accent,
  isComplete,
  children,
}: {
  step: number | string;
  title: string;
  subtitle?: string;
  accent: string;
  isComplete?: boolean;
  children: React.ReactNode;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: isComplete ? `${accent}60` : "divider",
      borderRadius: 3,
      mb: 3,
      overflow: "hidden",
      transition: "border-color 0.3s, box-shadow 0.3s",
      "&:hover": { boxShadow: `0 2px 16px ${accent}12` },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(135deg, ${accent}0a 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {step}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {isComplete !== undefined &&
        (isComplete ? (
          <CheckCircleIcon fontSize="small" sx={{ color: "#4caf50" }} />
        ) : (
          <RadioButtonUncheckedIcon fontSize="small" sx={{ color: "#bdbdbd" }} />
        ))}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

const BulkUploadChallenges = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [mode, setMode] = useState<UploadMode>("individual");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  const copy = MODE_COPY[mode];
  const accent = copy.accent;

  const onModeChange = (_: any, value: UploadMode | null) => {
    if (!value) return;
    setMode(value);
    setFile(null);
    setResult(null);
  };

  const onDownloadTemplate = async () => {
    try {
      setLoading(true);
      await downloadChallengeTemplate(mode);
    } catch (error: any) {
      showSnackbar(error.message || "Failed to download template", "error");
    } finally {
      setLoading(false);
    }
  };

  const onUpload = async () => {
    if (!file) return;
    try {
      setLoading(true);
      setResult(null);
      const resp = await bulkUploadChallenges(mode, file);
      const data: BulkResult = resp?.data;
      setResult(data);

      if (data?.success) {
        showSnackbar(`Successfully created ${data.created} challenge(s).`, "success");
        setFile(null);
      } else {
        showSnackbar(
          `Upload failed validation — ${data?.errors?.length ?? 0} issue(s) found. See the failure log below.`,
          "error",
        );
      }
    } catch (error: any) {
      showSnackbar(error.message || "Bulk upload failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentShell
      title="Bulk Upload Challenges"
      loading={loading}
      actions={
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={onModeChange}
          sx={{
            "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 2 },
            "& .Mui-selected": {
              color: `${accent} !important`,
              borderColor: `${accent}88 !important`,
              background: `${accent}14 !important`,
            },
          }}
        >
          {(Object.keys(MODE_COPY) as UploadMode[]).map((m) => (
            <ToggleButton key={m} value={m}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {MODE_COPY[m].icon}
                {MODE_COPY[m].label}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
    >
      <Box sx={{ maxWidth: 920, mx: "auto", py: 1 }}>
        {/* Step 1 — template */}
        <SectionCard
          step={1}
          title="Download the template"
          subtitle={`${copy.label} challenges`}
          accent={accent}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {copy.description}
          </Typography>
          <CustomButton startIcon={<ArrowDownwardIcon />} onClick={onDownloadTemplate}>
            Download {copy.label} Template
          </CustomButton>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2 }}>
            <DescriptionOutlinedIcon fontSize="small" sx={{ color: "text.disabled", mt: "2px" }} />
            <Typography variant="caption" color="text.secondary">
              The template ships with example rows and an Instructions sheet that
              lists every field, whether it is required, and the accepted values.
            </Typography>
          </Box>
        </SectionCard>

        {/* Step 2 — upload */}
        <SectionCard
          step={2}
          title="Upload your completed file"
          subtitle="The whole file is validated before anything is created"
          accent={accent}
          isComplete={Boolean(file)}
        >
          <FilePicker
            label={copy.fileLabel}
            name="bulkChallengeFile"
            fileType=".xlsx, .xls"
            value={file}
            showPreview={false}
            onFileSelect={(f: any) => {
              setFile(f || null);
              setResult(null);
            }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            All-or-nothing: if any row is invalid, <b>nothing</b> is created and
            you get an exact row-and-field failure log below.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <CustomButton
              startIcon={<CloudUploadIcon />}
              disabled={!file}
              onClick={onUpload}
            >
              Upload &amp; Create
            </CustomButton>
          </Box>
        </SectionCard>

        {/* Results */}
        {result &&
          (result.success ? (
            <Alert
              icon={<CheckCircleIcon />}
              severity="success"
              sx={{ borderRadius: 3, alignItems: "center" }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                Created {result.created} challenge(s)
              </AlertTitle>
              They now appear in the Challenges list.
              <Box sx={{ mt: 1.5 }}>
                <CustomButton
                  onClick={() => navigate(configData.routes.challenges.challenges)}
                >
                  View Challenges
                </CustomButton>
              </Box>
            </Alert>
          ) : (
            <Paper
              elevation={0}
              sx={{ border: "1px solid", borderColor: "#f4433655", borderRadius: 3, overflow: "hidden" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  py: 2,
                  borderLeft: "4px solid #f44336",
                  borderBottom: "1px solid",
                  borderBottomColor: "divider",
                  background: "linear-gradient(135deg, #f4433610 0%, transparent 100%)",
                }}
              >
                <ErrorOutlineIcon sx={{ color: "#f44336" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Upload failed — nothing was created
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Fix the rows below and upload again.
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color="error"
                  label={`${result?.errors?.length ?? 0} issue(s)`}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["Row", "Sheet", "Combo", "Field", "Problem"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, background: "#fafafa" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(result.errors ?? []).map((err, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{err.row ?? "—"}</TableCell>
                        <TableCell>{err.sheet ?? "—"}</TableCell>
                        <TableCell>{err.comboGroup ?? "—"}</TableCell>
                        <TableCell>
                          {err.field ? (
                            <Chip size="small" variant="outlined" label={err.field} />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell sx={{ color: "error.main" }}>{err.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
      </Box>
    </ContentShell>
  );
};

export default BulkUploadChallenges;
